import { ComponentType } from 'react';
import { FixedSizeListProps, FixedSizeList as _FixedSizeList_ } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Button, Input } from '@artstorefronts/ui';
import { Search, Copy, Loader2, Edit, DiamondMinus, ExternalLink } from 'lucide-react';
import { useQuery, gql, useMutation } from '@apollo/client';
import { toast } from 'sonner';
import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AdminLayout from '../../../components/AdminLayout';

const QUERY = gql`
  query GetUsers($limit: Int, $offset: Int, $search: String) {
    users(limit: $limit, offset: $offset, search: $search) {
      users {
        asfId
        firstName
        lastName
        email
        tokens
        permissions {
          id
          key
        }
        stripeCustomerId
        createdAt
        updatedAt
        isOldAsfMember
        numImages
        numChats
        numSocialPosts
        hasSettingsInstructions
      }
      total
    }
  }
`;

const CLEAR_TOKENS_MUTATION = gql`
  mutation ClearTokens($asfId: ID!, $key: String!) {
    clearTokens(asfId: $asfId, key: $key)
  }
`;

const List = _FixedSizeList_ as unknown as ComponentType<FixedSizeListProps>;

export default function UsersPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const PAGE_SIZE = 50;

  const { data, loading, fetchMore, refetch } = useQuery(QUERY, {
    variables: { limit: PAGE_SIZE, offset: 0 },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const allUsers = data?.users?.users || [];
  const totalUsers = data?.users?.total || 0;
  const hasMoreData = !search && allUsers.length < totalUsers && totalUsers > 0;

  const loadMoreUsers = useCallback(async () => {
    if (!hasMoreData || loading || loadingMore || allUsers.length >= totalUsers) return;

    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          limit: PAGE_SIZE,
          offset: allUsers.length,
          search: undefined, // Only load more when not searching
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.users?.users?.length) {
            return prev;
          }

          // Prevent duplicate users by checking if we already have them
          const existingIds = new Set(prev.users.users.map((user: any) => user.asfId));
          const newUsers = fetchMoreResult.users.users.filter((user: any) => !existingIds.has(user.asfId));

          if (newUsers.length === 0) {
            return prev;
          }

          const updatedUsers = [...prev.users.users, ...newUsers];

          return {
            users: {
              ...prev.users,
              users: updatedUsers,
              total: Math.max(prev.users.total, fetchMoreResult.users.total),
            },
          };
        },
      });
    } catch (error) {
      console.error('Error loading more users:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchMore, allUsers.length, hasMoreData, loading, loadingMore, totalUsers]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      const wasInputFocused = document.activeElement === searchInputRef.current;

      if (search && search.trim()) {
        // When searching, get all results (no pagination)
        await refetch({
          search: search.trim(),
          limit: undefined,
          offset: undefined,
        });
      } else {
        // When not searching, reset to paginated browsing
        await refetch({
          search: undefined,
          limit: PAGE_SIZE,
          offset: 0,
        });
      }

      // Restore focus if input was focused before refetch
      if (wasInputFocused && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [search, refetch]);

  // No client-side filtering - server handles it
  const filteredUsers = allUsers;

  const Row = ({ index, style }: any) => {
    const [clearTokens] = useMutation(CLEAR_TOKENS_MUTATION, {
      refetchQueries: [
        {
          query: QUERY,
          variables: search ? { search: search.trim() } : { limit: PAGE_SIZE, offset: 0 },
        },
      ],
    });
    const [isClearing, setIsClearing] = useState(false);
    const user = filteredUsers[index];

    // Load more data when approaching the end of the list (only when not searching)
    useEffect(() => {
      if (!search && index >= allUsers.length - 5 && hasMoreData && !loadingMore) {
        loadMoreUsers();
      }
    }, [index, allUsers.length, hasMoreData, loadMoreUsers, search, loadingMore]);

    const handleClearTokens = async () => {
      try {
        setIsClearing(true);
        await clearTokens({
          variables: {
            asfId: user.asfId.toString(),
            key: 'spark',
          },
        });
        toast.success(t('users.toast.tokens-cleared'));
      } catch (error) {
        console.error(error);
        toast.error(t('users.toast.tokens-clear-failed'));
      } finally {
        setIsClearing(false);
      }
    };

    const createdAt = new Date(user.createdAt);

    return (
      <div style={style} className="border-b">
        <div className="grid grid-cols-[120px_240px_90px_90px_90px_90px_90px_90px_90px_80px_1fr] items-center px-4 py-3 text-xs">
          <div className="flex items-center gap-2">
            <div className="font-medium">{user.asfId}</div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(user.asfId.toString());
                toast.success(t('users.toast.asf-id-copied'));
              }}
              className="text-zinc-500 hover:text-zinc-700"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 pr-4">
            <div className="truncate" title={user.email}>
              {user.email}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(user.email);
                toast.success(t('users.toast.email-copied'));
              }}
              className="text-zinc-500 hover:text-zinc-700"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
          <div className="text-center">{user?.tokens?.sparks || 0}</div>
          <div className="text-center">{user?.numImages || 0}</div>
          <div className="text-center">{user?.numChats || 0}</div>
          <div className="text-center">{user?.numSocialPosts || 0}</div>
          <div className="text-center">{user?.hasSettingsInstructions ? tc('yes') : tc('no')}</div>
          <div className="text-center">{user.isOldAsfMember ? tc('yes') : tc('no')}</div>
          <div className="text-center flex items-center justify-center">
            {user.stripeCustomerId ? (
              <a
                href={`https://dashboard.stripe.com/customers/${user.stripeCustomerId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-700"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            ) : (
              tc('no')
            )}
          </div>
          <div className="text-right">{createdAt.toLocaleDateString()}</div>
          <div className="flex justify-end gap-4 text-center">
            <Button
              variant="link"
              size="sm"
              asChild
              className="text-xs p-0 h-auto text-blue-500 hover:text-blue-700"
              title={t('users.edit-user')}
            >
              <Link to={`/admin/users/${user.asfId}/edit`}>
                <Edit className="h-4 w-4" />
              </Link>
            </Button>
            <Button
              variant="link"
              size="sm"
              onClick={handleClearTokens}
              disabled={isClearing}
              className="text-xs text-blue-500 hover:text-blue-700 p-0 h-auto"
              title={t('users.clear-tokens')}
            >
              {isClearing ? (
                <div className="flex items-center gap-1">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : (
                <DiamondMinus className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout title={t('users.page-title')} description={t('users.page-description')}>
      <>
        <div className="flex items-center justify-between mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              placeholder={t('users.search-placeholder')}
              className="pl-8"
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          <div className="text-sm text-muted-foreground">{t('users.count', { count: totalUsers })}</div>
        </div>

        <div className="rounded-md border bg-white overflow-hidden">
          <div className="bg-zinc-50 border-b">
            <div className="grid grid-cols-[120px_240px_90px_90px_90px_90px_90px_90px_90px_80px_1fr] py-3 px-4 text-xs font-medium">
              <div>{t('users.table.asf-id')}</div>
              <div>{t('users.table.email')}</div>
              <div className="text-center">{t('users.table.spark-usage')}</div>
              <div className="text-center">{t('users.table.images')}</div>
              <div className="text-center">{t('users.table.chats')}</div>
              <div className="text-center">{t('users.table.posts')}</div>
              <div className="text-center">{t('users.table.instructions')}</div>
              <div className="text-center">{t('users.table.asf-member')}</div>
              <div className="text-center">{t('users.table.stripe-link')}</div>
              <div className="text-right">{t('users.table.created')}</div>
              <div className="text-right">{t('users.table.actions')}</div>
            </div>
          </div>
          <div style={{ height: 'calc(100dvh - 500px)' }}>
            <AutoSizer>
              {({ height, width }) => (
                <List height={height} itemCount={filteredUsers.length} itemSize={40} width={width}>
                  {Row}
                </List>
              )}
            </AutoSizer>
          </div>
          {loadingMore && (
            <div className="flex items-center justify-center py-1">
              <span className="text-xs text-muted-foreground">{tc('loading')}</span>
            </div>
          )}
        </div>
      </>
    </AdminLayout>
  );
}
